import {
  SHOPPER_SOUND_VOLUME_DEFAULT,
  type StorefrontSounds,
  shopperVolumeGain,
} from "@loot-os/api-contract";
import type { EndUser } from "@loot-os/client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useLootMe } from "../templates/pages/shared/accountHeader.js";
import { lootFetch, qk } from "./queries.js";
import type { TenantConfig } from "./tenant.js";

const MUTED_KEY = "loot-sound-muted";
const VOLUME_KEY = "loot-sound-volume";

/** The shopper's mute and volume (0 to 100). */
export interface SoundPreference {
  muted: boolean;
  volume: number;
  /** Unmuting at volume 0 restores the default, so "on" is never silent. */
  setMuted: (muted: boolean) => void;
  /** 0 mutes; any louder level unmutes, since moving the slider means "let me hear it". */
  setVolume: (volume: number) => void;
}

interface Level {
  muted: boolean;
  volume: number;
}

/** What storage could not hold (blocked or full), so the page still honors the shopper's choice. */
let memory: Level | null = null;
const listeners = new Set<() => void>();

function clampVolume(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** The browser's level as one string (a stable snapshot), read fresh so another tab's write shows. */
function readLocalKey(): string {
  try {
    const muted = window.localStorage.getItem(MUTED_KEY) === "true";
    const raw = window.localStorage.getItem(VOLUME_KEY);
    const parsed = raw === null || raw.trim() === "" ? Number.NaN : Number(raw);
    const volume = Number.isFinite(parsed) ? clampVolume(parsed) : SHOPPER_SOUND_VOLUME_DEFAULT;
    return `${muted}|${volume}`;
  } catch {
    return memory ? `${memory.muted}|${memory.volume}` : `false|${SHOPPER_SOUND_VOLUME_DEFAULT}`;
  }
}

function parseLocalKey(key: string): Level {
  const [muted, volume] = key.split("|");
  return { muted: muted === "true", volume: Number(volume) };
}

function writeLocal(level: Level) {
  memory = level;
  try {
    window.localStorage.setItem(MUTED_KEY, String(level.muted));
    window.localStorage.setItem(VOLUME_KEY, String(level.volume));
  } catch {
    // Blocked storage: the in-memory level carries this page view.
  }
  for (const notify of listeners) notify();
}

function subscribeLocal(onChange: () => void): () => void {
  listeners.add(onChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === MUTED_KEY || event.key === VOLUME_KEY) onChange();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener("storage", onStorage);
  };
}

function levelOf(user: EndUser): Level {
  return { muted: user.soundMuted, volume: user.soundVolume };
}

/** The level a change asks for, from the current one, by Boxed's rules. */
function nextLevel(current: Level, change: { muted: boolean } | { volume: number }): Level {
  if ("volume" in change) {
    const volume = clampVolume(change.volume);
    return { volume, muted: volume === 0 };
  }
  if (!change.muted && current.volume === 0) {
    return { volume: SHOPPER_SOUND_VOLUME_DEFAULT, muted: false };
  }
  return { volume: current.volume, muted: change.muted };
}

/**
 * The shopper's sound level. Signed in, the account's values win (and are saved there, optimistically);
 * until `/v1/me` answers, and signed out, the browser's. Local values are never copied to the
 * account, so a shared computer's setting cannot overwrite someone's own.
 */
export function useSoundPreference(options: { onError?: () => void } = {}): SoundPreference {
  const me = useLootMe();
  const queryClient = useQueryClient();
  const localKey = useSyncExternalStore(subscribeLocal, readLocalKey, readLocalKey);
  const local = useMemo(() => parseLocalKey(localKey), [localKey]);
  const current: Level = me ? levelOf(me) : local;
  // The setters read the latest values through refs, so their identity never changes: they sit in
  // the opener's context memo, and a new one per render would re-render the tree mid-spin.
  const latest = useRef({ me, local, onError: options.onError });
  latest.current = { me, local, onError: options.onError };
  // Each save sends both fields, so two in flight could reach the API out of order and leave the
  // older level saved. The screen moves at once, but the PUTs go one at a time. A save belongs to
  // the account that asked for it: if another shopper signs in while it waits, it is dropped.
  const saves = useRef<Promise<void>>(Promise.resolve());
  const pending = useRef(0);
  const wanted = useRef<{ accountId: string; level: Level } | null>(null);

  /** Puts a level on the account's cached `me`, and on no other account's. */
  const show = useCallback(
    (accountId: string, level: Level) =>
      queryClient.setQueryData<EndUser | undefined>(qk.lootMe, (old) =>
        old && old.id === accountId
          ? { ...old, soundMuted: level.muted, soundVolume: level.volume }
          : old,
      ),
    [queryClient],
  );

  const put = useCallback(
    async (accountId: string, base: Level, next: Level) => {
      try {
        // The cache entry can be gone while `me` is held (a reset mid-flight): fall back to it.
        const owner = queryClient.getQueryData<EndUser>(qk.lootMe) ?? latest.current.me;
        if (owner?.id !== accountId) return;
        await lootFetch<unknown>("/me/settings", {
          method: "PUT",
          body: JSON.stringify({ soundMuted: next.muted, soundVolume: next.volume }),
        });
      } catch {
        // Undo only this write's level, and only if no later write has replaced it.
        if (wanted.current?.level === next) {
          wanted.current = null;
          show(accountId, base);
        }
        latest.current.onError?.();
      } finally {
        // A refetch while later saves wait would show an older level for a moment.
        pending.current -= 1;
        if (pending.current === 0) void queryClient.invalidateQueries({ queryKey: qk.lootMe });
      }
    },
    [queryClient, show],
  );
  const apply = useCallback(
    (change: { muted: boolean } | { volume: number }) => {
      const account = latest.current.me;
      if (!account) {
        writeLocal(nextLevel(latest.current.local, change));
        return;
      }
      // While saves wait, the level they will leave is the one to build on; the cache can lag it.
      const waiting = pending.current > 0 && wanted.current?.accountId === account.id;
      // The cache entry can be gone while `me` is held (a reset mid-flight): fall back to it.
      const before = queryClient.getQueryData<EndUser>(qk.lootMe) ?? account;
      const base = waiting && wanted.current ? wanted.current.level : levelOf(before);
      const next = nextLevel(base, change);
      wanted.current = { accountId: account.id, level: next };
      show(account.id, next);
      // An in-flight refetch would land after the optimistic value and overwrite it, and cancelling
      // it puts back the value from before it started, so the wanted level is shown again after.
      void queryClient
        .cancelQueries({ queryKey: qk.lootMe })
        .then(() => wanted.current && show(wanted.current.accountId, wanted.current.level));
      pending.current += 1;
      const turn = saves.current.then(() => put(account.id, base, next));
      saves.current = turn.catch(() => undefined);
    },
    [queryClient, put, show],
  );
  const setMuted = useCallback((muted: boolean) => void apply({ muted }), [apply]);
  const setVolume = useCallback((volume: number) => void apply({ volume }), [apply]);

  return { muted: current.muted, volume: current.volume, setMuted, setVolume };
}

/** The store's sounds, read fail-safe: a config cached before sounds shipped has none. */
export function soundsOf(config: TenantConfig): StorefrontSounds {
  return (config as Partial<TenantConfig>).sounds ?? NO_SOUNDS;
}
const NO_SOUNDS: StorefrontSounds = {};

/**
 * Plays the store's "Good pull" sound (its battle one when only that has a file) at the shopper's
 * new level, so the slider can be judged by ear. A store with no such file plays nothing.
 */
export function playTestSound(sounds: StorefrontSounds, volume: number) {
  const sound = [sounds["opener.reveal.rare"], sounds["battle.reveal.rare"]].find(
    (s): s is { url: string; gainDb: number } => s !== undefined && "url" in s,
  );
  if (!sound || volume <= 0) return;
  try {
    const audio = new Audio(sound.url);
    audio.volume = Math.min(1, shopperVolumeGain(volume) * 10 ** (sound.gainDb / 20));
    void audio.play().catch(() => undefined);
  } catch {
    // No audio output: the slider still saved.
  }
}
